// Accordion Configuration
const ACCORDION_CONFIG = {
  ANIMATION_DURATION: 400,
  SCROLL_OFFSET: 120,
  ANIMATION_TIMEOUT_BUFFER: 50,
  SELECTORS: {
    panel: '.Accordion .etx-accordion__content, .etx-accordion__content',
    list: '.etx-accordion-list',
    accordion: '.etx-accordion',
    header: '.etx-accordion__header[data-accordion-clickable]',
    content: '.etx-accordion__content',
  },
};

// Accordion Manager Class
class AccordionManager {
  constructor(config = ACCORDION_CONFIG) {
    this.config = config;
    this.animatingPanels = new WeakSet();
    this.activeTimeouts = new Set();
    this.eventListeners = [];
    this.initialized = false;
  }

  isAnimating(panel) {
    return this.animatingPanels.has(panel);
  }

  lock(panel) {
    this.animatingPanels.add(panel);
  }

  unlock(panel) {
    this.animatingPanels.delete(panel);
  }

  measurePanelHeight(panel) {
    const prevHeight = panel.style.height;
    const prevTransition = panel.style.transition;

    panel.style.transition = 'none';
    panel.style.height = 'auto';
    const height = panel.getBoundingClientRect().height;
    panel.style.height = prevHeight;

    panel.offsetHeight;
    panel.style.transition = prevTransition;

    return height;
  }

  openPanel(accordion, panel, duration = this.config.ANIMATION_DURATION) {
    if (!panel || this.isAnimating(panel)) return;

    accordion.classList.add('active');
    accordion.setAttribute('data-accordion-opened', '');

    // Sofort öffnen ohne Animation
    if (duration === 0) {
      panel.style.transition = 'none';
      panel.style.height = 'auto';
      panel.style.overflow = 'hidden';
      return;
    }

    const targetHeight = this.measurePanelHeight(panel);
    this.lock(panel);

    panel.style.transition = `height ${duration}ms ease`;
    panel.style.height = '0px';

    requestAnimationFrame(() => {
      panel.style.height = `${targetHeight}px`;
    });

    const cleanup = () => {
      panel.style.transition = '';
      panel.style.height = 'auto';
      panel.style.overflow = 'hidden';
      this.unlock(panel);
      panel.removeEventListener('transitionend', onTransitionEnd);
      this.activeTimeouts.delete(fallbackTimeout);
    };

    const onTransitionEnd = (e) => {
      if (e.target === panel && e.propertyName === 'height') {
        clearTimeout(fallbackTimeout);
        cleanup();
      }
    };

    panel.addEventListener('transitionend', onTransitionEnd);

    const fallbackTimeout = setTimeout(() => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      cleanup();
    }, duration + this.config.ANIMATION_TIMEOUT_BUFFER);

    this.activeTimeouts.add(fallbackTimeout);
  }

  closePanel(accordion, panel, duration = this.config.ANIMATION_DURATION) {
    if (!panel || this.isAnimating(panel)) return;

    accordion.classList.remove('active');
    accordion.removeAttribute('data-accordion-opened');

    // Sofort schliessen ohne Animation
    if (duration === 0) {
      panel.style.transition = 'none';
      panel.style.height = '0px';
      panel.style.overflow = 'hidden';
      return;
    }

    const currentHeight =
      panel.style.height === 'auto'
        ? this.measurePanelHeight(panel)
        : panel.getBoundingClientRect().height;

    this.lock(panel);

    panel.style.transition = `height ${duration}ms ease`;
    panel.style.height = `${currentHeight}px`;

    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });

    const cleanup = () => {
      panel.style.transition = '';
      panel.style.overflow = 'hidden';
      this.unlock(panel);
      panel.removeEventListener('transitionend', onTransitionEnd);
      this.activeTimeouts.delete(fallbackTimeout);
    };

    const onTransitionEnd = (e) => {
      if (e.target === panel && e.propertyName === 'height') {
        clearTimeout(fallbackTimeout);
        cleanup();
      }
    };

    panel.addEventListener('transitionend', onTransitionEnd);

    const fallbackTimeout = setTimeout(() => {
      panel.removeEventListener('transitionend', onTransitionEnd);
      cleanup();
    }, duration + this.config.ANIMATION_TIMEOUT_BUFFER);

    this.activeTimeouts.add(fallbackTimeout);
  }

  closeOthers(list, currentAccordion) {
    const activeAccordions = list.querySelectorAll(`${this.config.SELECTORS.accordion}.active`);

    activeAccordions.forEach((accordion) => {
      if (accordion === currentAccordion) return;

      const panel = accordion.querySelector(this.config.SELECTORS.content);
      if (panel) {
        this.closePanel(accordion, panel);
      }
    });
  }

  handleAccordionClick(event) {
    const header = event.target.closest(this.config.SELECTORS.header);
    if (!header) return;

    const accordion = header.closest(this.config.SELECTORS.accordion);
    const list = header.closest(this.config.SELECTORS.list);

    if (!accordion || !list) return;

    const panel = accordion.querySelector(this.config.SELECTORS.content);
    if (!panel || this.isAnimating(panel)) return;

    const isActive = accordion.classList.contains('active');
    const allowMultiple = list.hasAttribute('data-accordion-multiple');
    const shouldScrollUp = list.hasAttribute('data-accordion-scrollup');

    if (isActive) {
      this.closePanel(accordion, panel);
    } else {
      if (!allowMultiple) {
        this.closeOthers(list, accordion);
      }

      this.openPanel(accordion, panel);

      if (shouldScrollUp) {
        const scrollDelay = this.config.ANIMATION_DURATION + this.config.ANIMATION_TIMEOUT_BUFFER;
        const timeoutId = setTimeout(() => {
          const offset =
            accordion.getBoundingClientRect().top + window.scrollY - this.config.SCROLL_OFFSET;
          window.scrollTo({ top: offset, behavior: 'smooth' });
          this.activeTimeouts.delete(timeoutId);
        }, scrollDelay);

        this.activeTimeouts.add(timeoutId);
      }
    }
  }

  initialize() {
    if (this.initialized) {
      console.warn('etxAccordion already initialized');
      return;
    }

    const panels = document.querySelectorAll(this.config.SELECTORS.panel);
    panels.forEach((panel) => {
      panel.style.overflow = 'hidden';
      panel.style.height = '0px';
    });

    document.querySelectorAll(this.config.SELECTORS.list).forEach((list) => {
      const accordions = Array.from(list.querySelectorAll(this.config.SELECTORS.accordion));
      const openedAccordions = accordions.filter((accordion) =>
        accordion.hasAttribute('data-accordion-opened')
      );
      const allowMultiple = list.hasAttribute('data-accordion-multiple');

      if (allowMultiple) {
        openedAccordions.forEach((accordion) => {
          accordion.classList.add('active');
          const panel = accordion.querySelector(this.config.SELECTORS.content);
          if (panel) {
            this.openPanel(accordion, panel, 0);
          }
        });

        accordions.forEach((accordion) => {
          if (accordion.hasAttribute('data-accordion-opened')) return;

          accordion.classList.remove('active');
          const panel = accordion.querySelector(this.config.SELECTORS.content);
          if (panel) {
            panel.style.height = '0px';
            panel.style.overflow = 'hidden';
          }
        });
      } else {
        const firstOpenedAccordion = openedAccordions[0];

        if (firstOpenedAccordion) {
          firstOpenedAccordion.classList.add('active');
          const panel = firstOpenedAccordion.querySelector(this.config.SELECTORS.content);
          if (panel) {
            this.openPanel(firstOpenedAccordion, panel, 0);
          }
        }

        accordions.forEach((accordion) => {
          if (accordion === firstOpenedAccordion) return;

          accordion.classList.remove('active');
          accordion.removeAttribute('data-accordion-opened');

          const panel = accordion.querySelector(this.config.SELECTORS.content);
          if (panel) {
            panel.style.height = '0px';
            panel.style.overflow = 'hidden';
          }
        });
      }
    });

    const boundHandler = this.handleAccordionClick.bind(this);
    document.addEventListener('click', boundHandler);

    this.eventListeners.push({
      target: document,
      type: 'click',
      handler: boundHandler,
    });

    this.initialized = true;
  }

  destroy() {
    this.eventListeners.forEach(({ target, type, handler }) => {
      target.removeEventListener(type, handler);
    });
    this.eventListeners = [];

    this.activeTimeouts.forEach(clearTimeout);
    this.activeTimeouts.clear();

    this.animatingPanels = new WeakSet();
    this.initialized = false;
  }
}

function etxAccordion() {
  if (!window.etxAccordionManager) {
    window.etxAccordionManager = new AccordionManager();
  }

  window.etxAccordionManager.initialize();

  return window.etxAccordionManager;
}

window.etxAccordion = etxAccordion;